import { useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  CalendarClock,
  Search,
  ArrowLeft,
  ArrowRight,
  Check,
  User,
  Building2,
  AlertTriangle,
  Clock,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { cn } from '@/lib/utils.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '@/components/ui'

// ─── Types ───────────────────────────────────────────────────────────────────

interface Ticket {
  id: string
  tanssId: string
  subject: string
  status: string
  priority: string
  assignedToEmployeeName?: string | null
  assignedToEmployeeId?: number | string | null
  customer?: { name?: string; id?: number | string } | null
  companyName?: string
  createdAt: string
}

interface Slot {
  start: string
  end: string
}

interface BusyInterval {
  start: string
  end: string
  source: 'appointment' | 'm365'
  title?: string
}

interface AvailabilityResponse {
  ticket: {
    tanssId: string
    subject: string
    customerName: string | null
    technicianTanssId: string | null
    technicianName: string | null
  }
  technician: {
    tanssId: string | null
    name: string | null
    userId: string | null
    hasCalendarConnection: boolean
  }
  workingHours: { startHour: number; endHour: number }
  slots: Slot[]
  busy: BusyInterval[]
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateInput(d: Date) {
  return d.toISOString().slice(0, 10)
}

function addDays(d: Date, days: number) {
  const r = new Date(d)
  r.setDate(r.getDate() + days)
  return r
}

function formatTimeRange(startIso: string, endIso: string) {
  const s = new Date(startIso)
  const e = new Date(endIso)
  const fmt = (x: Date) =>
    `${String(x.getHours()).padStart(2, '0')}:${String(x.getMinutes()).padStart(2, '0')}`
  return `${fmt(s)} – ${fmt(e)}`
}

function formatDateLong(iso: string) {
  return new Date(iso).toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

/** Group slots by day (YYYY-MM-DD local). */
function groupSlotsByDay(slots: Slot[]): Array<{ day: string; slots: Slot[] }> {
  const map = new Map<string, Slot[]>()
  for (const s of slots) {
    const key = new Date(s.start).toISOString().slice(0, 10)
    const list = map.get(key)
    if (list) list.push(s)
    else map.set(key, [s])
  }
  return Array.from(map.entries())
    .map(([day, slots]) => ({ day, slots }))
    .sort((a, b) => a.day.localeCompare(b.day))
}

// ─── Step 1: Ticket-Auswahl ──────────────────────────────────────────────────

function TicketPicker({ onPick }: { onPick: (t: Ticket) => void }) {
  const [search, setSearch] = useState('')
  const [quickFilter, setQuickFilter] = useState<'mine' | 'open' | 'unassigned'>('open')

  const qs = new URLSearchParams()
  if (search) qs.set('search', search)
  qs.set('quick', quickFilter)
  qs.set('pageSize', '50')

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['terminbuchung-tickets', search, quickFilter],
    queryFn: () => apiClient.get<{ data: Ticket[] }>(`/tickets?${qs.toString()}`).then((r) => r.data),
  })

  const tickets = data?.data ?? []

  return (
    <Card>
      <Card.Header>
        <Card.Title>1. Ticket auswählen</Card.Title>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ticket suchen (Betreff, Firma, Nr.)"
              leadingIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="w-48">
            <Select<'mine' | 'open' | 'unassigned'>
              value={quickFilter}
              onValueChange={(v) => setQuickFilter(v)}
              options={[
                { value: 'open', label: 'Offene Tickets' },
                { value: 'mine', label: 'Meine Tickets' },
                { value: 'unassigned', label: 'Nicht zugewiesen' },
              ]}
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingState label="Tickets werden geladen …" />
        ) : isError ? (
          <ErrorState
            title="Tickets konnten nicht geladen werden"
            description={(error as Error)?.message ?? 'Unbekannter Fehler'}
            onRetry={() => refetch()}
          />
        ) : tickets.length === 0 ? (
          <EmptyState
            icon={<Search className="w-10 h-10" />}
            title="Keine Tickets gefunden"
            description="Passe Suche oder Filter an."
          />
        ) : (
          <div className="divide-y divide-gray-200 rounded-md border border-gray-200 overflow-hidden max-h-[480px] overflow-y-auto">
            {tickets.map((t) => {
              const techName = t.assignedToEmployeeName
              const customer = t.customer?.name || t.companyName
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onPick(t)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-caption text-gray-500">
                        #{t.tanssId}
                      </span>
                      <StatusBadge status={t.status} />
                    </div>
                    <div className="text-body text-gray-900 font-medium truncate">
                      {t.subject}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-small text-gray-600">
                      {customer && (
                        <span className="inline-flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {customer}
                        </span>
                      )}
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          techName ? 'text-gray-600' : 'text-amber-700',
                        )}
                      >
                        <User className="w-3.5 h-3.5" />
                        {techName || 'Kein Techniker zugewiesen'}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 mt-1 shrink-0" />
                </button>
              )
            })}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Step 2: Slot auswaehlen ──────────────────────────────────────────────────

function SlotPicker({
  ticket,
  onBack,
  onConfirm,
}: {
  ticket: Ticket
  onBack: () => void
  onConfirm: (args: { slot: Slot; availability: AvailabilityResponse }) => void
}) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const [rangeStart, setRangeStart] = useState<string>(toDateInput(today))
  const [rangeEnd, setRangeEnd] = useState<string>(toDateInput(addDays(today, 13)))
  const [duration, setDuration] = useState<number>(60)

  const payload = useMemo(
    () => ({
      ticketId: ticket.tanssId,
      fromDate: new Date(`${rangeStart}T00:00:00`).toISOString(),
      toDate: new Date(`${rangeEnd}T23:59:59`).toISOString(),
      durationMinutes: duration,
    }),
    [ticket.tanssId, rangeStart, rangeEnd, duration],
  )

  const {
    data: availability,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['availability', payload],
    queryFn: () =>
      apiClient
        .post<AvailabilityResponse>('/appointments/availability', payload)
        .then((r) => r.data),
  })

  const grouped = availability ? groupSlotsByDay(availability.slots) : []

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <Card.Title>2. Verfügbaren Termin wählen</Card.Title>
          <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft className="w-4 h-4" />} onClick={onBack}>
            Anderes Ticket
          </Button>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="rounded-md bg-gray-50 border border-gray-200 px-4 py-3">
            <div className="text-small text-gray-500">Ticket</div>
            <div className="text-body font-medium text-gray-900">
              <span className="font-mono text-caption text-gray-500 mr-2">
                #{ticket.tanssId}
              </span>
              {ticket.subject}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-small text-gray-600">
              <span className="inline-flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5" />
                {ticket.customer?.name || ticket.companyName || '—'}
              </span>
              <span className="inline-flex items-center gap-1">
                <User className="w-3.5 h-3.5" />
                {ticket.assignedToEmployeeName || 'Kein Techniker'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-small text-gray-700 mb-1">Von</label>
              <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} />
            </div>
            <div>
              <label className="block text-small text-gray-700 mb-1">Bis</label>
              <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} />
            </div>
            <div>
              <Select
                label="Dauer"
                value={String(duration)}
                onValueChange={(v) => setDuration(Number(v))}
                options={[
                  { value: '30', label: '30 Minuten' },
                  { value: '45', label: '45 Minuten' },
                  { value: '60', label: '60 Minuten' },
                  { value: '90', label: '90 Minuten' },
                  { value: '120', label: '2 Stunden' },
                  { value: '180', label: '3 Stunden' },
                  { value: '240', label: '4 Stunden' },
                ]}
              />
            </div>
          </div>

          {!ticket.assignedToEmployeeName && (
            <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5 text-small text-amber-900 flex gap-2 items-start">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Dem Ticket ist kein Techniker zugewiesen – es werden generische Slots innerhalb der
                Arbeitszeit angezeigt (ohne Kalender-Abgleich).
              </div>
            </div>
          )}

          {availability?.technician.userId && !availability.technician.hasCalendarConnection && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-2.5 text-small text-blue-900 flex gap-2 items-start">
              <Clock className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                Der Techniker hat keinen M365-Kalender verbunden – es werden nur bereits gebuchte
                Termine aus SystemhausManager als belegt markiert.
              </div>
            </div>
          )}

          {isLoading ? (
            <LoadingState label="Verfügbarkeit wird berechnet …" />
          ) : isError ? (
            <ErrorState
              title="Verfügbarkeit konnte nicht geladen werden"
              description={
                (error as { response?: { data?: { error?: string } }; message?: string })?.response
                  ?.data?.error ??
                (error as Error)?.message ??
                'Unbekannter Fehler'
              }
              onRetry={() => refetch()}
            />
          ) : grouped.length === 0 ? (
            <EmptyState
              icon={<CalendarClock className="w-10 h-10" />}
              title="Keine freien Slots im gewählten Zeitraum"
              description="Wähle einen anderen Zeitraum oder eine kürzere Dauer."
            />
          ) : (
            <div className="space-y-4">
              {grouped.map(({ day, slots }) => (
                <div key={day}>
                  <div className="text-small font-medium text-gray-900 mb-2">
                    {formatDateLong(day)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {slots.map((s) => (
                      <button
                        key={s.start}
                        type="button"
                        onClick={() =>
                          availability && onConfirm({ slot: s, availability })
                        }
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-small text-gray-800 hover:border-brand-500 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                      >
                        {formatTimeRange(s.start, s.end)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Step 3: Bestaetigen ──────────────────────────────────────────────────────

function ConfirmStep({
  ticket,
  slot,
  availability,
  onBack,
  onBooked,
}: {
  ticket: Ticket
  slot: Slot
  availability: AvailabilityResponse
  onBack: () => void
  onBooked: () => void
}) {
  const { toast } = useToast()
  const [title, setTitle] = useState(`Termin: ${ticket.subject}`)
  const [location, setLocation] = useState(ticket.customer?.name || ticket.companyName || '')
  const [description, setDescription] = useState('')
  const [addToCalendar, setAddToCalendar] = useState(true)

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.post('/appointments', {
        ticketId: ticket.tanssId,
        start: slot.start,
        end: slot.end,
        title,
        description,
        location,
        addToTechnicianCalendar: addToCalendar,
      }),
    onSuccess: () => {
      toast({
        tone: 'success',
        title: 'Termin gebucht',
        description: `${formatDateLong(slot.start)}, ${formatTimeRange(slot.start, slot.end)}`,
      })
      onBooked()
    },
    onError: (err: { response?: { data?: { error?: string; code?: string } }; message?: string }) => {
      toast({
        tone: 'danger',
        title: 'Buchung fehlgeschlagen',
        description: err.response?.data?.error ?? err.message ?? 'Unbekannter Fehler',
      })
    },
  })

  return (
    <Card>
      <Card.Header>
        <Card.Title>3. Termin bestätigen</Card.Title>
        <Button variant="ghost" size="sm" leadingIcon={<ArrowLeft className="w-4 h-4" />} onClick={onBack}>
          Anderen Slot wählen
        </Button>
      </Card.Header>
      <Card.Body className="space-y-4">
        <div className="rounded-md bg-brand-50 border border-brand-200 px-4 py-3">
          <div className="text-small text-brand-700">Gewählter Termin</div>
          <div className="text-body-lg font-medium text-gray-900">
            {formatDateLong(slot.start)}
          </div>
          <div className="text-body text-gray-700">{formatTimeRange(slot.start, slot.end)}</div>
          <div className="mt-2 text-small text-gray-700">
            Techniker: {availability.technician.name ?? '—'}
          </div>
        </div>

        <div>
          <label className="block text-small text-gray-700 mb-1">Titel</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>

        <div>
          <label className="block text-small text-gray-700 mb-1">Ort</label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="z. B. Vor-Ort bei Kunde / Remote"
          />
        </div>

        <div>
          <label className="block text-small text-gray-700 mb-1">Beschreibung (optional)</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Agenda, Vorbereitung, benötigte Geräte …"
          />
        </div>

        {availability.technician.hasCalendarConnection && (
          <label className="flex items-center gap-2 text-small text-gray-700">
            <input
              type="checkbox"
              checked={addToCalendar}
              onChange={(e) => setAddToCalendar(e.target.checked)}
              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Termin in M365-Kalender des Technikers eintragen
          </label>
        )}
      </Card.Body>
      <Card.Footer>
        <Button variant="secondary" onClick={onBack} disabled={mutation.isPending}>
          Zurück
        </Button>
        <Button
          variant="primary"
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          leadingIcon={<Check className="w-4 h-4" />}
        >
          Termin buchen
        </Button>
      </Card.Footer>
    </Card>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

type Step = 'ticket' | 'slot' | 'confirm' | 'done'

export function TerminbuchungPage() {
  const [step, setStep] = useState<Step>('ticket')
  const [ticket, setTicket] = useState<Ticket | null>(null)
  const [slot, setSlot] = useState<Slot | null>(null)
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null)

  function reset() {
    setStep('ticket')
    setTicket(null)
    setSlot(null)
    setAvailability(null)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <CalendarClock className="w-7 h-7 text-brand-500" />
            Terminbuchung
          </span>
        }
        description="Termin direkt mit dem zugewiesenen Techniker eines Tickets buchen."
      />

      <StepIndicator step={step} />

      {step === 'ticket' && (
        <TicketPicker
          onPick={(t) => {
            setTicket(t)
            setStep('slot')
          }}
        />
      )}

      {step === 'slot' && ticket && (
        <SlotPicker
          ticket={ticket}
          onBack={() => {
            setTicket(null)
            setStep('ticket')
          }}
          onConfirm={({ slot, availability }) => {
            setSlot(slot)
            setAvailability(availability)
            setStep('confirm')
          }}
        />
      )}

      {step === 'confirm' && ticket && slot && availability && (
        <ConfirmStep
          ticket={ticket}
          slot={slot}
          availability={availability}
          onBack={() => setStep('slot')}
          onBooked={() => setStep('done')}
        />
      )}

      {step === 'done' && ticket && slot && (
        <Card>
          <Card.Body className="text-center py-10 space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 text-green-700 flex items-center justify-center">
              <Check className="w-6 h-6" />
            </div>
            <div>
              <div className="text-h3 text-gray-900">Termin erfolgreich gebucht</div>
              <div className="text-body text-gray-600 mt-1">
                {formatDateLong(slot.start)}, {formatTimeRange(slot.start, slot.end)} · Ticket #
                {ticket.tanssId}
              </div>
            </div>
            <div className="flex justify-center gap-2">
              <Button variant="primary" onClick={reset}>
                Weiteren Termin buchen
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

function StepIndicator({ step }: { step: Step }) {
  const steps: Array<{ id: Step; label: string }> = [
    { id: 'ticket', label: 'Ticket' },
    { id: 'slot', label: 'Zeit' },
    { id: 'confirm', label: 'Bestätigen' },
  ]
  const activeIndex =
    step === 'done' ? steps.length : steps.findIndex((s) => s.id === step)

  return (
    <div className="flex items-center gap-2 text-small">
      {steps.map((s, i) => {
        const isActive = i === activeIndex
        const isDone = i < activeIndex
        return (
          <div key={s.id} className="flex items-center gap-2">
            <div
              className={cn(
                'w-6 h-6 rounded-full flex items-center justify-center text-caption font-medium',
                isDone
                  ? 'bg-brand-600 text-white'
                  : isActive
                    ? 'bg-brand-100 text-brand-700 ring-2 ring-brand-400'
                    : 'bg-gray-100 text-gray-500',
              )}
            >
              {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span
              className={cn(
                isActive ? 'text-gray-900 font-medium' : 'text-gray-500',
              )}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="text-gray-300">›</span>}
          </div>
        )
      })}
    </div>
  )
}
